import React, { useState, useEffect, useCallback } from "react";
import Drawer from "react-modern-drawer";
import useDashboardStore from "../../../../stores/dashboard";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import Checkbox from "../../../../components/Checkbox/Index";
import Select from "../../../../components/form/select";
import { isEntitySuperAdmin } from "../../../../../components/helper/helper";

import visa from "../../../../../../images/v2/visa24x24.svg";
import mastercard from "../../../../../../images/v2/master24x24.svg";
import discover from "../../../../../../images/v2/discover24x24.svg";
import amex from "../../../../../../images/v2/amex24x24.svg";
import debit from "../../../../../../images/v2/debit24x24.svg";
import ebt from "../../../../../../images/v2/ebt24x24.svg";
import Button from "../../../../components/new-button";
import useUserStore from "../../../../stores/user";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import { SearchOrganizations } from "../../../../../api";
import { useSavedFilters } from "../../../../hooks/useSavedFilters";
import SavedFiltersSection from "../../../../components/SavedFiltersSection/SavedFiltersSection";
import { isDashboardFilterSnapshotEmpty } from "../../../../utils/savedFilterSnapshotEmpty";
import "react-modern-drawer/dist/index.css";
import CloseIcon24 from "../../../../assets/svg/icons/common/CloseIcon24";
import styles from "./styles.module.scss";

const cards = [
  { id: 1, name: "Visa", icon: visa },
  { id: 2, name: "Mastercard", icon: mastercard },
  { id: 3, name: "Amex", icon: amex },
  { id: 4, name: "Discover", icon: discover },
  { id: 5, name: "Debit", icon: debit },
  { id: 6, name: "EBT", icon: ebt },
];

const SAVED_FILTER_PAGE = "dashboard";

const FilterDrawer = () => {
  const filterDrawerOpen = useDashboardStore((state) => state.filterDrawerOpen);
  const setFilterDrawerOpen = useDashboardStore(
    (state) => state.setFilterDrawerOpen,
  );
  const [selectedCards, setSelectedCards] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const setFilterData = useDashboardStore((state) => state.setFilterData);
  const filterData = useDashboardStore((state) => state.filterData);
  const filterOrgTemp = filterData.filterOrgTemp;
  const filterUserTemp = filterData.filterUserTemp;
  const goalFilterKeyTemp = filterData.goalFilterKeyTemp;

  const user = useUserStore((state) => state.user);
  const isGodUser = user?.is_pc_admin;
  const isOrgAdmin = user?.role === "admin";
  const isMobile = useMediaQuery(768);
  const isSmallMobile = useMediaQuery(420);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
        const res = await SearchOrganizations(null);
        if (!res.success) {
          console.error("There was a problem fetching organizations");
        }

        const orgsOptions = res?.data?.map((org) => ({
          label: org.name,
          value: org.id,
        }));
        setOrganizations(orgsOptions);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isEntitySuperAdmin(user)]);

  useEffect(() => {
    fetchData();
  }, [isEntitySuperAdmin(user), fetchData]);

  useEffect(() => {
    if (filterDrawerOpen) {
      setSelectedCards(filterData.cards ?? []);
    }
  }, [filterDrawerOpen]);

  const getCurrentFilterValues = () => ({
    cards: selectedCards,
    filterOrgTemp: filterOrgTemp || null,
    filterUserTemp: filterUserTemp || null,
    goalFilterKeyTemp: goalFilterKeyTemp || "",
  });

  const applyFilterValues = (filters) => {
    setSelectedCards(filters.cards || []);
    setFilterData({
      filterOrgTemp: filters.filterOrgTemp || null,
      filterUserTemp: filters.filterUserTemp || null,
      goalFilterKeyTemp: filters.goalFilterKeyTemp || "",
    });
  };

  const getAppliedSnapshot = () => {
    const fd = useDashboardStore.getState().filterData;
    return {
      cards: [...(fd.cards ?? [])],
      filterOrgTemp: fd.filterOrgTemp || null,
      filterUserTemp: fd.filterUserTemp || null,
      goalFilterKeyTemp: fd.goalFilterKeyTemp || "",
    };
  };

  const savedFiltersApi = useSavedFilters({
    page: SAVED_FILTER_PAGE,
    drawerOpen: filterDrawerOpen,
    getFilterSnapshot: getCurrentFilterValues,
    applyFilterSnapshot: applyFilterValues,
    isSnapshotEmpty: isDashboardFilterSnapshotEmpty,
    getAppliedSnapshot,
    onAfterDelete: () => {
      setSelectedCards([]);
      setFilterData({
        filterOrgTemp: "",
        filterUserTemp: "",
        goalFilterKeyTemp: "",
      });
    },
  });

  const handleOnSearchInputChange = async (term) => {
    try {
      const res = await SearchOrganizations({ term });
      if (!res.success) {
        console.error("There was a problem fetching organizations");
      }

      const orgsOptions = res?.data.map((org) => ({
        label: org.name,
        value: org.id,
      }));

      setOrganizations(orgsOptions);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const handleOnOrganizationSelectChange = (selectedOption) => {
    const option = selectedOption && (typeof selectedOption === "object"
      ? selectedOption
      : organizations.find((o) => o.value === selectedOption));
    const value = option?.value ?? selectedOption;
    setFilterData({
      filterOrgTemp: option || selectedOption || null,
      goalFilterKeyTemp: value ? `org-${value}` : "",
    });
  };

  return (
    <Drawer
      open={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
      direction={isMobile ? "left" : "right"}
      size={isSmallMobile ? "320px" : isMobile ? "375px" : "440px"}
      zIndex={8888}
      overlayColor={"#000"}
      overlayOpacity={0.5}
    >
      <div className={styles.filterDrawerContainer}>
        <div className={styles.drawer_title_block}>
          <div className={styles.filterDrawerHeader}>
            <ButtonIcon onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon24 />
            </ButtonIcon>
          </div>

          <div className={styles.filterDrawerTitle}>
            <h5>Filter</h5>
            {(selectedCards.length > 0 || filterOrgTemp || filterUserTemp) && (
              <div className={styles.filterChipsArea}>
              {filterOrgTemp && (
                <div className={styles.filterChip}>
                  {filterOrgTemp?.label ?? filterOrgTemp?.value ?? filterOrgTemp}
                  <button 
                    onClick={() => {
                      setFilterData({
                        filterOrgTemp: null,
                        goalFilterKeyTemp: "",
                      });
                    }}
                    className={styles.filterChipClose}
                  >
                    ×
                  </button>
                </div>
              )}
              {filterUserTemp && (
                <div className={styles.filterChip}>
                  {filterUserTemp?.label ?? filterUserTemp?.value ?? filterUserTemp}
                  <button 
                    onClick={() => {
                      setFilterData({
                        filterUserTemp: null,
                        goalFilterKeyTemp: "",
                      });
                    }}
                    className={styles.filterChipClose}
                  >
                    ×
                  </button>
                </div>
              )}
              {selectedCards.map((cardId) => {
                const card = cards.find(c => c.id === cardId);
                return (
                  <div key={`chip-card-${cardId}`} className={styles.filterChip}>
                    {card?.name}
                    <button 
                      onClick={() => {
                        setSelectedCards(
                          selectedCards.filter(id => id !== cardId)
                        );
                      }}
                      className={styles.filterChipClose}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </div>

        <div className={styles.filterContentContainer}>
          <SavedFiltersSection
            savedFilterOptions={savedFiltersApi.savedFilterOptions}
            selectedSavedFilter={savedFiltersApi.selectedSavedFilter}
            onSavedFilterChange={savedFiltersApi.handleSavedFilterSelect}
            onDeleteSavedFilter={savedFiltersApi.handleDeleteSavedFilter}
            isDeletingFilter={savedFiltersApi.isDeletingFilter}
            editFilterName={savedFiltersApi.editFilterName}
            onEditFilterNameChange={savedFiltersApi.setEditFilterName}
            onUpdateSavedFilter={savedFiltersApi.handleUpdateSavedFilter}
            isUpdatingFilter={savedFiltersApi.isUpdatingFilter}
            showSaveFilterInput={savedFiltersApi.showSaveFilterInput}
            onShowSaveInput={savedFiltersApi.openSaveFilterInput}
            newFilterName={savedFiltersApi.newFilterName}
            onNewFilterNameChange={savedFiltersApi.setNewFilterName}
            onSaveFilter={savedFiltersApi.handleSaveFilter}
            isSavingFilter={savedFiltersApi.isSavingFilter}
            onCancelNewFilter={() => {
              savedFiltersApi.cancelSaveFilterInput();
              savedFiltersApi.setNewFilterName("");
            }}
          />

          {/* Organization/User Filter Section - Only visible to org admins */}
          {(isEntitySuperAdmin(user)) && (
            <div className={styles.statusFilter}>
              <h5 className={styles.heading}>
                ORGANIZATION
              </h5>
              <div className={styles.filterContainer}>                  
                <Select
                  async
                  search
                  options={organizations}
                  value={filterOrgTemp}
                  onSearchChange={handleOnSearchInputChange}
                  onChange={handleOnOrganizationSelectChange}
                  placeholder="Search organizations"
                />                                  
              </div>
            </div>
          )}

          <div className={styles.statusFilter}>
            <h5 className={styles.heading}>CARDS</h5>
            <div className={styles.filterList}>
              {cards.map((card) => (
                <label
                  key={`card-${card.id}`}
                  className={styles.filterListItem}
                  htmlFor={`card-${card.id}`}
                >
                  <Checkbox
                    name={`card-${card.id}`}
                    id={`card-${card.id}`}
                    value={`card-${card.id}`}
                    onChange={() => {
                      if (selectedCards.includes(card.id)) {
                        setSelectedCards(
                          selectedCards.filter((id) => id !== card.id),
                        );
                      } else {
                        setSelectedCards([...selectedCards, card.id]);
                      }
                    }}
                    checked={selectedCards.includes(card.id)}
                  />

                  {card.icon && (
                    <div className={styles.filterListItemIcon}>
                      <img src={card.icon} />
                    </div>
                  )}
                  <div className={styles.filterListItemLabel}>
                    <span>{card.name}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            variant="secondary"
            size="large"
            onClick={() => {
              setSelectedCards([]);
              setFilterData({
                filterUser: null,
                filterOrg: null,
                goalFilterKey: null,
                filterUserTemp: null,
                filterOrgTemp: null,
                goalFilterKeyTemp: "",
                cards: [],
              });
            }}
          >
            Clear
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={() => {
              const orgValue = filterOrgTemp && (typeof filterOrgTemp === "object" ? filterOrgTemp.value : filterOrgTemp);
              const userValue = filterUserTemp && (typeof filterUserTemp === "object" ? filterUserTemp.value : filterUserTemp);
              setFilterData({
                filterUser: userValue || null,
                filterOrg: orgValue || null,
                goalFilterKey: goalFilterKeyTemp || null,
                cards: selectedCards,
              });
              savedFiltersApi.commitAppliedSavedFilter();
              setFilterDrawerOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default FilterDrawer;
